import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { readResponseStatus } from '../../lib/api';
import {
  cancelEmailSignup,
  cancelGoogleOauthSignup,
  completeEmailSignup,
  completeGoogleOauthSignup,
  createDesktopSigninIntent,
  requestEmailLoginCode,
  restoreSignupInviteUrl,
  verifyEmailLoginCode,
} from '../../lib/auth_api';
import { getMetaContent, marketingUrl, rootDomainUrl } from '../../lib/meta';
import { classifySignupInvite, signupInviteCode } from '../../lib/signupInviteUrl';

import GoogleLogoIcon from '../../../assets/images/google-logo.svg';
import InviteArrowIcon from '../../../assets/images/invite-arrow.svg';

import useCountdown from '../../hooks/useCountdown';
import useDesktopWindowFocusSignal from '../../hooks/useDesktopWindowFocusSignal';
import AuthButton from './AuthButton';
import AuthCodeForm from './AuthCodeForm';
import {
  CODE_RESEND_COOLDOWN_SECONDS,
  formatCountdownSeconds,
  formatEmailCodeCooldownMessage,
} from './AuthCodeResendControl';
import AuthField from './AuthField';
import { AuthBackLink, AuthFormMessage, AuthHeader, AuthHeaderBlock, AuthPageStack } from './AuthFlow';
import { authPages } from './authPages';

const DEFAULT_SUCCESS_PATH = '/app/start';
const GOOGLE_AUTH_PATH = '/users/auth/google_oauth2';
const EMAIL_CODE_LENGTH = 6;
const FORM_MESSAGE_ID = 'auth-form-message';
const CODE_ERROR_MESSAGE_ID = 'auth-code-error-message';
const WAITLIST_PATH = '/#waitlist';
const INVALID_INVITATION_MESSAGE = 'That code is invalid or already used.';
const INVALID_INVITE_URL_MESSAGE = 'Invalid invite URL';
const EXPIRED_EMAIL_SIGNUP_MESSAGE = 'Your email verification expired. Start again with your email.';
const EXPIRED_GOOGLE_SIGNUP_MESSAGE = 'Your Google sign-in expired. Continue with Google to start again.';
const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';
const RATE_LIMITED_MESSAGE = 'Too many attempts. Please try again later.';
const OFFLINE_MESSAGE = 'Could not reach the server. Please try again.';

function initialValuesFor(fields) {
  const values = {};

  fields.forEach((field) => {
    values[field.name] = '';
  });

  return values;
}

function allFieldsFor(page) {
  return [...page.fields, ...(page.codeFields || [])];
}

function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

function sanitizeOtpCode(code) {
  return code.replace(/\D/g, '').slice(0, EMAIL_CODE_LENGTH);
}

function sanitizeEmail(email) {
  return email.toString().trim();
}

function isObviouslyInvalidEmail(email) {
  return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sanitizeEmail(email));
}

function validationMessages(validations, name) {
  const messages = validations?.[name];
  return Array.isArray(messages) ? messages : [];
}

function hasValidationMessages(validations) {
  if (!validations) return false;

  return Object.values(validations).some((messages) => Array.isArray(messages) && messages.length > 0);
}

function baseErrorFor(validations) {
  const messages = validationMessages(validations, 'base');
  if (messages.length === 0) return null;
  if (messages.includes('is invalid or expired')) return 'Code is invalid or expired.';
  if (messages.includes('is invalid')) return 'The submitted credentials are invalid.';

  return messages.join(', ');
}

function hasBaseCodeError(validations) {
  return validationMessages(validations, 'base').includes('is invalid or expired');
}

function invalidSignupInviteMessage(value, expectedKind) {
  const candidate = value?.trim();
  if (!candidate) return expectedKind === 'url' ? 'Paste an invite URL.' : 'Enter your invite code.';
  if (expectedKind === 'url') return INVALID_INVITE_URL_MESSAGE;
  if (expectedKind === 'code') return 'Invite codes are 6 characters.';
  if (/^(?:https?:\/\/|www\.)/i.test(candidate) || candidate.includes('/chat/')) return INVALID_INVITE_URL_MESSAGE;

  return 'Invite codes are 6 characters.';
}

function fieldErrorFor(field, validations) {
  const messages = validationMessages(validations, field.name);
  if (messages.length === 0) return null;

  return `${field.label || field.name} ${messages.join(', ')}`;
}

function inlineFieldErrorFor(field, validations) {
  const messages = validationMessages(validations, field.name);
  if (messages.length === 0) return null;
  if (field.name === 'email' && messages.includes('is invalid')) return 'Not a valid email';

  return null;
}

function fieldHasError(pageKey, field, validations) {
  if (validationMessages(validations, field.name).length > 0) return true;
  if (pageKey === 'login' && hasBaseCodeError(validations)) return field.name === 'code';

  return false;
}

function validationSummaryFor(page, validations) {
  const baseError = baseErrorFor(validations);
  if (baseError) return baseError;

  return page.fields
    .map((field) => fieldErrorFor(field, validations))
    .filter(Boolean)
    .join(' ');
}

function responseErrorMessage(page, response, validations) {
  if (readResponseStatus(response) === 429) return RATE_LIMITED_MESSAGE;

  const validationSummary = validationSummaryFor(page, validations);
  if (validationSummary) return validationSummary;
  if (hasValidationMessages(validations)) return 'Please check the highlighted fields.';

  return GENERIC_ERROR_MESSAGE;
}

function isSignupInvitationRequiredError(errors) {
  return errors?.signup_invitation_required === true;
}

function isSignupInvitationInvalidError(errors) {
  return errors?.signup_invitation_invalid === true;
}

// Either the code we sent was rejected or the server wants one it has not been given.
function needsInvitationCode(errors) {
  return isSignupInvitationInvalidError(errors) || isSignupInvitationRequiredError(errors);
}

function isEmailCodeCreateCooldown(errors) {
  return errors?.['rate_limited:email_code_create_email_cooldown'] === true;
}

function clientValidationsFor(page, values) {
  const nextValidations = {};

  page.fields.forEach((field) => {
    if (field.name === 'email' && isObviouslyInvalidEmail(values.email || '')) {
      nextValidations.email = ['is invalid'];
    }
  });

  return nextValidations;
}

function AuthForm({ disabledLabel, error, isDisabled, isSubmitting, message, onChange, onSubmit, page, pageKey, validations, values }) {
  const hasInlineFieldError = page.fields.some((field) => inlineFieldErrorFor(field, validations));
  const needsCustomValidation = page.fields.some((field) => field.type === 'email');

  return (
    <form className="flex w-full flex-col items-start gap-5" onSubmit={onSubmit} noValidate={needsCustomValidation}>
      <div className="flex w-full flex-col items-start gap-4">
        {page.fields.map((field) => {
          const errorMessage = inlineFieldErrorFor(field, validations);

          return (
            <AuthField
              disabled={isDisabled}
              errorMessage={errorMessage}
              errorMessageId={FORM_MESSAGE_ID}
              field={field}
              hasError={fieldHasError(pageKey, field, validations)}
              key={field.name}
              onChange={onChange}
              value={values[field.name]}
            />
          );
        })}
      </div>
      <div className="flex w-full flex-col gap-3">
        <AuthFormMessage error={hasInlineFieldError ? null : error} id={FORM_MESSAGE_ID} message={message} />
        <AuthButton disabled={isDisabled}>
          {isSubmitting ? page.pendingLabel || page.submitLabel : disabledLabel || page.submitLabel}
        </AuthButton>
      </div>
    </form>
  );
}

function EmailCodeForm({
  code,
  error,
  isDisabled,
  isResendingCode,
  isSubmitting,
  onCodeChange,
  onResendCode,
  onSubmit,
  page,
  resendCooldownSeconds,
  validations,
}) {
  const hasInvalidCodeError = hasBaseCodeError(validations);
  const hasCodeError = fieldHasError('login', { name: 'code' }, validations);

  return (
    <AuthCodeForm
      code={code}
      codeErrorMessageId={CODE_ERROR_MESSAGE_ID}
      codeLength={EMAIL_CODE_LENGTH}
      disabled={isDisabled}
      error={error}
      formMessageId={FORM_MESSAGE_ID}
      hasCodeError={hasCodeError}
      isResending={isResendingCode}
      isSubmitting={isSubmitting}
      onCodeChange={onCodeChange}
      onResend={onResendCode}
      onSubmit={onSubmit}
      pendingLabel={page.pendingLabel || page.submitLabel}
      resendCooldownSeconds={resendCooldownSeconds}
      showInlineCodeError={hasInvalidCodeError}
      submitLabel={page.submitLabel}
    />
  );
}

function GoogleAuthButton() {
  const csrfToken = getMetaContent('csrf-token') || '';
  // Inside the Electron shell the webview cannot host Google consent (Google
  // blocks embedded user agents), so delegate to the shell bridge: it runs the
  // system-browser sign-in flow and then navigates this view to the session
  // handoff URL. See docs/desktop-oauth-shell-contract.md.
  const desktopOauthBridge = typeof window.electron?.oauth?.signInWithGoogle === 'function' ? window.electron.oauth : null;
  const [isDelegating, setIsDelegating] = useState(false);
  const awaitingDesktopReturnRef = useRef(false);
  const desktopFocusSignal = useDesktopWindowFocusSignal();

  const handleDesktopSignIn = useCallback(
    async function handleDesktopGoogleSignIn() {
      if (!desktopOauthBridge || isDelegating) return;

      setIsDelegating(true);

      try {
        // Bind the eventual session handoff to THIS view's browser session: the
        // server stores the intent nonce in the session and seals it into the
        // sign-in ticket, so a forwarded one-time session URL cannot log a
        // different browser in (login CSRF defense).
        const { response, payload } = await createDesktopSigninIntent();
        const signinIntent = payload?.signin_intent;
        if (!response.ok || !signinIntent) throw new Error('failed to create signin intent');

        // Arm the focus-driven re-enable only after the shell confirms the browser
        // handoff — a focus event landing while the bridge call is in flight must
        // not unlock a duplicate attempt (same pattern as the gallery and modal).
        const desktopOauth = await desktopOauthBridge.signInWithGoogle({ signinIntent });
        if (desktopOauth?.completed) {
          if (!desktopOauth.ok) setIsDelegating(false);
        } else {
          awaitingDesktopReturnRef.current = true;
        }
      } catch {
        setIsDelegating(false);
      }
    },
    [desktopOauthBridge, isDelegating],
  );

  useEffect(
    function reenableGoogleSignInOnDesktopReturn() {
      // A completed sign-in navigates this view away; a cancelled or abandoned
      // one returns focus here, so re-enable the button for a retry.
      if (!awaitingDesktopReturnRef.current) return;

      awaitingDesktopReturnRef.current = false;
      setIsDelegating(false);
    },
    [desktopFocusSignal],
  );

  if (desktopOauthBridge) {
    return (
      <AuthButton type="button" variant="primary" disabled={isDelegating} onClick={handleDesktopSignIn}>
        <GoogleLogoIcon className="size-7 shrink-0" />
        Continue with Google
      </AuthButton>
    );
  }

  return (
    <form method="post" action={GOOGLE_AUTH_PATH} className="w-full">
      <input type="hidden" name="authenticity_token" value={csrfToken} readOnly />
      <AuthButton type="submit" variant="primary">
        <GoogleLogoIcon className="size-7 shrink-0" />
        Continue with Google
      </AuthButton>
    </form>
  );
}

function SignupInvitationHeader({ description, title }) {
  return (
    <div className="flex w-full flex-col items-center gap-6 text-center">
      <h1 className="w-full text-[28px] leading-6 font-semibold tracking-[-0.56px] text-black">{title}</h1>
      {description ? <p className="w-full text-[13px] leading-5 font-normal text-gray-500">{description}</p> : null}
    </div>
  );
}

function SignupInvitationOption({ children, href, onClick, showArrow = false }) {
  const classes =
    'flex w-full cursor-pointer items-center justify-between rounded-xl border-[0.5px] border-gray-200 bg-gray-100 p-5 text-left text-[15px] leading-[18px] font-normal text-black outline-none hover:bg-gray-200 focus-visible:ring-2 focus-visible:ring-gray-400';
  const content = (
    <>
      <span>{children}</span>
      {showArrow ? <InviteArrowIcon aria-hidden="true" className="size-[18px] shrink-0" /> : null}
    </>
  );

  if (href) {
    return (
      <a className={classes} data-app-region="none" href={href}>
        {content}
      </a>
    );
  }

  return (
    <button className={classes} data-app-region="none" onClick={onClick} type="button">
      {content}
    </button>
  );
}

export function SignupInvitationChoice({ onBack, onCode, onUrl, waitlistHref }) {
  const invitationPage = authPages.signupInvitation;

  return (
    <AuthPageStack>
      <SignupInvitationHeader title={invitationPage.title} />
      <div className="flex w-full flex-col items-center gap-12">
        <div className="flex w-full flex-col gap-4">
          <SignupInvitationOption onClick={onUrl} showArrow>
            {invitationPage.options.url}
          </SignupInvitationOption>
          <SignupInvitationOption onClick={onCode} showArrow>
            {invitationPage.options.code}
          </SignupInvitationOption>
          <SignupInvitationOption href={waitlistHref}>{invitationPage.options.waitlist}</SignupInvitationOption>
        </div>
        <AuthBackLink className="leading-5" label={invitationPage.backLabel} onClick={onBack} />
      </div>
    </AuthPageStack>
  );
}

export function SignupInvitationForm({ error, isDisabled, isSubmitting, kind, onBack, onChange, onPaste, onSubmit, value }) {
  const invitationPage = authPages.signupInvitation;
  const method = invitationPage.methods[kind];

  return (
    <AuthPageStack>
      <SignupInvitationHeader description={method.description} title={method.title} />
      <div className="flex w-full flex-col items-center gap-12">
        <form className="flex w-full flex-col items-start gap-6" onSubmit={onSubmit} noValidate>
          <AuthField
            disabled={isDisabled}
            errorMessage={error}
            field={method.field}
            hasError={Boolean(error)}
            onChange={onChange}
            onPaste={onPaste}
            value={value}
          />
          <AuthButton className="text-[13px]" disabled={isDisabled || value.trim().length === 0}>
            {isSubmitting ? invitationPage.pendingLabel : invitationPage.submitLabel}
          </AuthButton>
        </form>
        <AuthBackLink className="leading-5" disabled={isDisabled} label={invitationPage.methodBackLabel} onClick={onBack} />
      </div>
    </AuthPageStack>
  );
}

export default function AuthPage({
  initialSignupInvitationEmail = '',
  initialSignupInvitationMode = '',
  initialSignupInvitationRequired = false,
  pageKey,
  successUrl,
}) {
  const page = authPages[pageKey];
  const initialInvitationMode = initialSignupInvitationMode || (initialSignupInvitationRequired ? 'google' : 'email');
  // Login opens on a Google/email choice and collapses the email form until the user picks it.
  const hasEntryState = pageKey === 'login';
  const [emailFormOpen, setEmailFormOpen] = useState(false);
  const [loginCodeRequested, setLoginCodeRequested] = useState(false);
  const [values, setValues] = useState(() => ({
    ...initialValuesFor(allFieldsFor(page)),
    ...(initialInvitationMode === 'email' ? { email: initialSignupInvitationEmail } : {}),
  }));
  const [validations, setValidations] = useState({});
  const [formError, setFormError] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResendingCode, setIsResendingCode] = useState(false);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useCountdown(0);
  const [lastEmailCodeRequestEmail, setLastEmailCodeRequestEmail] = useState('');
  // The invitation form shows after Google has verified the identity or after a
  // new email address has passed its OTP challenge.
  const [invitationRequired, setInvitationRequired] = useState(initialSignupInvitationRequired);
  const [invitationValue, setInvitationValue] = useState('');
  const [invitationError, setInvitationError] = useState(null);
  const [invitationMode, setInvitationMode] = useState(initialInvitationMode);
  const [invitationKind, setInvitationKind] = useState(null);
  const submitInFlightRef = useRef(false);
  const resendInFlightRef = useRef(false);

  const openEmailForm = useCallback(function openEmailFormCallback() {
    setEmailFormOpen(true);
  }, []);

  const closeEmailForm = useCallback(function closeEmailFormCallback() {
    setEmailFormOpen(false);
    setLoginCodeRequested(false);
    setValues((currentValues) => ({ ...currentValues, code: '' }));
    setValidations({});
    setFormError(null);
    setStatusMessage(null);
    setIsResendingCode(false);
  }, []);

  const backFromLoginCode = useCallback(function backFromLoginCodeCallback() {
    setLoginCodeRequested(false);
    setValues((currentValues) => ({ ...currentValues, code: '' }));
    setValidations({});
    setFormError(null);
    setStatusMessage(null);
    setIsResendingCode(false);
  }, []);

  const backFromInvitation = useCallback(
    function backFromInvitationCallback() {
      // Drop the server-side pending signup too, or reloading /login lands the
      // user right back on this form until it expires.
      if (invitationMode === 'google') cancelGoogleOauthSignup();
      else cancelEmailSignup();
      setInvitationRequired(false);
      setEmailFormOpen(false);
      setLoginCodeRequested(false);
      setValues((currentValues) => ({ ...currentValues, code: '' }));
      setInvitationValue('');
      setInvitationError(null);
      setInvitationKind(null);
    },
    [invitationMode],
  );

  const showInvitationUrlForm = useCallback(function showInvitationUrlFormCallback() {
    setInvitationKind('url');
    setInvitationValue('');
    setInvitationError(null);
  }, []);

  const showInvitationCodeForm = useCallback(function showInvitationCodeFormCallback() {
    setInvitationKind('code');
    setInvitationValue('');
    setInvitationError(null);
  }, []);

  const backFromInvitationForm = useCallback(function backFromInvitationFormCallback() {
    setInvitationKind(null);
    setInvitationValue('');
    setInvitationError(null);
  }, []);

  const showEmailInvitationForm = useCallback(function showEmailInvitationFormCallback(error = null) {
    setInvitationMode('email');
    setLoginCodeRequested(false);
    setValues((currentValues) => ({ ...currentValues, code: '' }));
    setInvitationValue('');
    setInvitationError(error);
    setInvitationKind(error ? 'code' : null);
    setInvitationRequired(true);
  }, []);

  const handleChange = useCallback(function handleChangeCallback(event) {
    const { name, value } = event.target;

    setValues((currentValues) => ({ ...currentValues, [name]: value }));
    setValidations((currentValidations) => {
      if (!currentValidations[name] && !currentValidations.base) return currentValidations;

      const nextValidations = { ...currentValidations };
      delete nextValidations[name];
      delete nextValidations.base;
      return nextValidations;
    });
    setFormError(null);
    setStatusMessage(null);
  }, []);

  const handleCodeChange = useCallback(function handleCodeChangeCallback(nextCode) {
    const code = sanitizeOtpCode(nextCode);

    setValues((currentValues) => ({ ...currentValues, code }));
    setValidations((currentValidations) => {
      if (!currentValidations.code && !currentValidations.base) return currentValidations;

      const nextValidations = { ...currentValidations };
      delete nextValidations.code;
      delete nextValidations.base;
      return nextValidations;
    });
    setFormError(null);
    setStatusMessage(null);
  }, []);

  const formPage = useMemo(
    function currentFormPage() {
      if (pageKey !== 'login') return page;

      if (!loginCodeRequested) {
        return emailFormOpen ? { ...page, title: page.emailTitle } : page;
      }

      return {
        ...page,
        title: page.codeTitle,
        fields: page.codeFields,
        submitLabel: page.codeSubmitLabel,
        pendingLabel: page.codePendingLabel,
      };
    },
    [emailFormOpen, loginCodeRequested, page, pageKey],
  );

  const normalizedEmailValue = sanitizeEmail(values.email || '');
  const isWaitingForSameEmailCode =
    pageKey === 'login' &&
    !loginCodeRequested &&
    resendCooldownSeconds > 0 &&
    normalizedEmailValue.length > 0 &&
    normalizedEmailValue === lastEmailCodeRequestEmail;
  const emailCodeCooldownMessage = isWaitingForSameEmailCode
    ? formatEmailCodeCooldownMessage(resendCooldownSeconds)
    : null;

  const submitGoogleInvitation = useCallback(
    async function submitGoogleInvitationCallback(code, setError) {
      const {
        response,
        errors: responseErrors,
        payload,
        validations: responseValidations,
      } = await completeGoogleOauthSignup({ signupInvitationCode: code });

      if (response.ok) {
        window.location.assign(payload?.redirect_url || successUrl || rootDomainUrl(DEFAULT_SUCCESS_PATH));
        return;
      }

      if (readResponseStatus(response) === 429) setError(RATE_LIMITED_MESSAGE);
      else if (needsInvitationCode(responseErrors)) setError(INVALID_INVITATION_MESSAGE);
      else if (hasBaseCodeError(responseValidations)) setError(EXPIRED_GOOGLE_SIGNUP_MESSAGE);
      else setError(responseErrorMessage(page, response, responseValidations));
    },
    [page, successUrl],
  );

  // Email mode reaches this point only after the OTP has already been verified.
  const submitEmailInvitation = useCallback(
    async function submitEmailInvitationCallback(code, setError) {
      const {
        response,
        errors: responseErrors,
        payload,
        validations: responseValidations,
      } = await completeEmailSignup({ signupInvitationCode: code });

      if (response.ok) {
        window.location.assign(payload?.redirect_url || successUrl || rootDomainUrl(DEFAULT_SUCCESS_PATH));
        return;
      }

      if (readResponseStatus(response) === 429) setError(RATE_LIMITED_MESSAGE);
      else if (needsInvitationCode(responseErrors)) setError(INVALID_INVITATION_MESSAGE);
      else if (hasBaseCodeError(responseValidations)) setError(EXPIRED_EMAIL_SIGNUP_MESSAGE);
      else setError(responseErrorMessage(page, response, responseValidations));
    },
    [page, successUrl],
  );

  const submitInvitation = useCallback(
    async function submitInvitationCallback(value) {
      if (submitInFlightRef.current) return;

      const invite = classifySignupInvite(value);
      if (!invite || invite.kind !== invitationKind) {
        setInvitationError(invalidSignupInviteMessage(value, invitationKind));
        return;
      }

      setInvitationValue(invite.value);
      setIsSubmitting(true);
      submitInFlightRef.current = true;
      setInvitationError(null);

      try {
        if (invite.kind === 'url') {
          const { response, validations: responseValidations } = await restoreSignupInviteUrl({ inviteUrl: invite.value });
          if (!response.ok) {
            const hasInviteUrlValidation = validationMessages(responseValidations, 'invite_url').length > 0;
            setInvitationError(
              hasInviteUrlValidation ? INVALID_INVITE_URL_MESSAGE : responseErrorMessage(page, response, responseValidations),
            );
            return;
          }
        }

        await (invitationMode === 'google'
          ? submitGoogleInvitation(invite.kind === 'code' ? invite.value : undefined, setInvitationError)
          : submitEmailInvitation(invite.kind === 'code' ? invite.value : undefined, setInvitationError));
      } catch {
        setInvitationError(OFFLINE_MESSAGE);
      } finally {
        submitInFlightRef.current = false;
        setIsSubmitting(false);
      }
    },
    [invitationKind, invitationMode, page, submitEmailInvitation, submitGoogleInvitation],
  );

  const handleInvitationChange = useCallback(
    function handleInvitationChangeCallback(event) {
      const value = event.target.value;
      setInvitationValue(value);
      setInvitationError(null);

      const completeCode = invitationKind === 'code' ? signupInviteCode(value) : null;
      if (completeCode) submitInvitation(completeCode);
    },
    [invitationKind, submitInvitation],
  );

  const handleInvitationPaste = useCallback(
    function handleInvitationPasteCallback(event) {
      const pastedValue = event.clipboardData?.getData('text/plain') || event.clipboardData?.getData('text');
      const invite = classifySignupInvite(pastedValue);
      if (!invite || invite.kind !== invitationKind) return;

      event.preventDefault();
      setInvitationValue(invite.value);
      setInvitationError(null);
      submitInvitation(invite.value);
    },
    [invitationKind, submitInvitation],
  );

  const handleInvitationSubmit = useCallback(
    function handleInvitationSubmitCallback(event) {
      event.preventDefault();
      submitInvitation(invitationValue);
    },
    [invitationValue, submitInvitation],
  );

  const handleSubmit = useCallback(
    async function handleSubmitCallback(event) {
      event.preventDefault();
      if (submitInFlightRef.current) return;

      // Source the submitted values from the DOM rather than relying solely on React state.
      // Password managers (1Password, etc.) fill inputs by setting the value directly, which
      // does not always round-trip through a controlled input's onChange — reading FormData
      // guarantees we submit exactly what the user sees, even when autofill bypassed React.
      const formData = new FormData(event.currentTarget);
      const submittedValues = { ...values };
      formPage.fields.forEach((field) => {
        const domValue = formData.get(field.name);
        if (typeof domValue === 'string') submittedValues[field.name] = domValue;
      });
      if (typeof submittedValues.email === 'string') submittedValues.email = sanitizeEmail(submittedValues.email);
      if (pageKey === 'login' && loginCodeRequested) submittedValues.code = sanitizeOtpCode(submittedValues.code || '');
      setValues(submittedValues);

      if (pageKey === 'login' && !loginCodeRequested && submittedValues.email === lastEmailCodeRequestEmail && resendCooldownSeconds > 0) {
        setStatusMessage(formatEmailCodeCooldownMessage(resendCooldownSeconds));
        setValidations({});
        setFormError(null);
        return;
      }

      const clientValidations = clientValidationsFor(formPage, submittedValues);
      if (hasValidationMessages(clientValidations)) {
        setStatusMessage(null);
        setValidations(clientValidations);
        setFormError(null);
        return;
      }

      if (pageKey === 'login' && loginCodeRequested && submittedValues.code.length !== EMAIL_CODE_LENGTH) {
        setStatusMessage(null);
        setValidations({ code: ['is incomplete'] });
        setFormError('Enter the 6-digit code.');
        return;
      }

      setIsSubmitting(true);
      submitInFlightRef.current = true;
      setValidations({});
      setFormError(null);
      setStatusMessage(null);

      try {
        const {
          response,
          errors: responseErrors,
          payload,
          validations: responseValidations,
        } = loginCodeRequested
          ? await verifyEmailLoginCode({ email: submittedValues.email, code: submittedValues.code, timeZone: browserTimeZone() })
          : await requestEmailLoginCode({ email: submittedValues.email });

        if (!response.ok) {
          if (needsInvitationCode(responseErrors)) {
            // "Invalid" means a code we already carried was rejected (used
            // meanwhile); "required" just means we have not asked for one yet.
            showEmailInvitationForm(isSignupInvitationInvalidError(responseErrors) ? INVALID_INVITATION_MESSAGE : null);
            return;
          }

          const isCreateCooldown = !loginCodeRequested && isEmailCodeCreateCooldown(responseErrors);
          if (!loginCodeRequested && readResponseStatus(response) === 429) {
            setLastEmailCodeRequestEmail(submittedValues.email);
            setResendCooldownSeconds(CODE_RESEND_COOLDOWN_SECONDS);
          }
          setValidations(responseValidations || {});
          setFormError(isCreateCooldown ? null : responseErrorMessage(formPage, response, responseValidations));
          setStatusMessage(
            isCreateCooldown ? formatEmailCodeCooldownMessage(CODE_RESEND_COOLDOWN_SECONDS) : null,
          );
          return;
        }

        if (!loginCodeRequested) {
          setLoginCodeRequested(true);
          setValues((currentValues) => ({
            ...currentValues,
            email: submittedValues.email,
            code: payload?.development_code || '',
          }));
          setLastEmailCodeRequestEmail(submittedValues.email);
          setResendCooldownSeconds(CODE_RESEND_COOLDOWN_SECONDS);
          return;
        }

        window.location.assign(payload?.redirect_url || successUrl || rootDomainUrl(DEFAULT_SUCCESS_PATH));
      } catch {
        setFormError(OFFLINE_MESSAGE);
      } finally {
        submitInFlightRef.current = false;
        setIsSubmitting(false);
      }
    },
    [
      formPage,
      lastEmailCodeRequestEmail,
      loginCodeRequested,
      pageKey,
      resendCooldownSeconds,
      setResendCooldownSeconds,
      showEmailInvitationForm,
      successUrl,
      values,
    ],
  );

  const handleResendCode = useCallback(
    async function handleResendCodeCallback() {
      if (!values.email || isSubmitting || isResendingCode || resendCooldownSeconds > 0 || resendInFlightRef.current) return;

      setIsResendingCode(true);
      resendInFlightRef.current = true;
      setValidations({});
      setFormError(null);
      setStatusMessage(null);

      try {
        const { response, payload, validations: responseValidations } = await requestEmailLoginCode({
          email: values.email,
        });

        if (!response.ok) {
          if (readResponseStatus(response) === 429) {
            setLastEmailCodeRequestEmail(sanitizeEmail(values.email));
            setResendCooldownSeconds(CODE_RESEND_COOLDOWN_SECONDS);
          }
          setValidations(responseValidations || {});
          setFormError(responseErrorMessage(formPage, response, responseValidations));
          return;
        }

        setValues((currentValues) => ({ ...currentValues, code: payload?.development_code || '' }));
        setLastEmailCodeRequestEmail(sanitizeEmail(values.email));
        setResendCooldownSeconds(CODE_RESEND_COOLDOWN_SECONDS);
      } catch {
        setFormError(OFFLINE_MESSAGE);
      } finally {
        resendInFlightRef.current = false;
        setIsResendingCode(false);
      }
    },
    [formPage, isResendingCode, isSubmitting, resendCooldownSeconds, setResendCooldownSeconds, values.email],
  );

  if (invitationRequired) {
    if (!invitationKind) {
      return (
        <SignupInvitationChoice
          onBack={backFromInvitation}
          onCode={showInvitationCodeForm}
          onUrl={showInvitationUrlForm}
          waitlistHref={marketingUrl(WAITLIST_PATH)}
        />
      );
    }

    return (
      <SignupInvitationForm
        error={invitationError}
        isDisabled={isSubmitting}
        isSubmitting={isSubmitting}
        kind={invitationKind}
        onBack={backFromInvitationForm}
        onChange={handleInvitationChange}
        onPaste={handleInvitationPaste}
        onSubmit={handleInvitationSubmit}
        value={invitationValue}
      />
    );
  }

  if (hasEntryState && !emailFormOpen) {
    return (
      <AuthPageStack>
        <AuthHeader title={formPage.title} />
        <div className="flex w-full flex-col gap-6">
          <GoogleAuthButton />
          <AuthButton onClick={openEmailForm} type="button" variant="secondary">
            Continue with email
          </AuthButton>
        </div>
      </AuthPageStack>
    );
  }

  if (hasEntryState && loginCodeRequested) {
    return (
      <AuthPageStack>
        <AuthHeaderBlock title={formPage.title}>
          We emailed you a log in code to <span className="font-medium text-black">{values.email}</span>
        </AuthHeaderBlock>
        <div className="flex w-full flex-col items-center gap-6">
          <EmailCodeForm
            code={values.code}
            error={formError}
            isDisabled={isSubmitting || isResendingCode}
            isResendingCode={isResendingCode}
            isSubmitting={isSubmitting}
            onCodeChange={handleCodeChange}
            onResendCode={handleResendCode}
            onSubmit={handleSubmit}
            page={formPage}
            resendCooldownSeconds={resendCooldownSeconds}
            validations={validations}
          />
          <AuthBackLink label={page.codeBackLabel} onClick={backFromLoginCode} />
        </div>
      </AuthPageStack>
    );
  }

  return (
    <AuthPageStack>
      <AuthHeader title={formPage.title} />
      <div className="flex w-full flex-col items-center gap-6">
        <AuthForm
          disabledLabel={emailCodeCooldownMessage ? formatCountdownSeconds(resendCooldownSeconds) : null}
          error={formError}
          isDisabled={isSubmitting || isResendingCode || isWaitingForSameEmailCode}
          isSubmitting={isSubmitting}
          message={statusMessage || emailCodeCooldownMessage}
          onChange={handleChange}
          onSubmit={handleSubmit}
          page={formPage}
          pageKey={pageKey}
          validations={validations}
          values={values}
        />
        <AuthBackLink label={page.backLabel} onClick={closeEmailForm} />
      </div>
    </AuthPageStack>
  );
}
